import React, {Component} from 'react';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';
import ReactPlayer from 'react-player';
import {createPortal} from 'react-dom';
import styles from './modal-video.module.scss';

const propTypes = {
  isOpen: PropTypes.bool,
  channel: PropTypes.string,
  ratio: PropTypes.string,
  animationSpeed: PropTypes.number,
  videoId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  aria: PropTypes.shape({
    openMessage: PropTypes.string,
    dismissBtnMessage: PropTypes.string
  }),
  renderNestedModal: PropTypes.bool,
  transcript: PropTypes.string
};

export default class ModalVideo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false
    };
    this.closeModal = this.closeModal.bind(this);
    this.updateFocus = this.updateFocus.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.keydownHandler.bind(this));
  }

  static getDerivedStateFromProps(props) {
    return {isOpen: props.isOpen};
  }

  componentDidUpdate() {
    const {isOpen} = this.state;
    if (isOpen && this.modal) {
      this.modal.focus();
      document.querySelector('html').classList.add('non-scrollable');
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.keydownHandler.bind(this));
  }

  static getVideoUrl(channel, videoId) {
    switch (channel) {
      case 'youtube':
        return `//www.youtube.com/embed/${videoId}`;
      case 'vimeo':
        return `//player.vimeo.com/video/${videoId}`;
      case 'wistia':
      default:
        return `//fast.wistia.com/medias/${videoId}`;
    }
  }

  static getPadding(ratio) {
    const arr = ratio.split(':');
    const width = Number(arr[0]);
    const height = Number(arr[1]);
    const padding = (height * 100) / width;
    return `${padding}%`;
  }

  updateFocus(e) {
    if (e.keyCode === 9) {
      e.preventDefault();
      e.stopPropagation();
      if (this.modal === document.activeElement) {
        this.modalbtn.focus();
      } else {
        this.modal.focus();
      }
    }
  }

  closeModal() {
    const {onClose} = this.props;
    this.setState({isOpen: false});
    if (typeof onClose === 'function') {
      onClose();
    }
    document.querySelector('html').classList.remove('non-scrollable');
  }

  keydownHandler(e) {
    if (e.keyCode === 27) {
      this.closeModal();
    }
  }

  render() {
    const {ratio, animationSpeed, aria, channel, videoId, renderNestedModal, transcript} =
      this.props;
    const {isOpen} = this.state;
    const [ratioWidth, ratioHeight] = ratio.split(':').map(Number);
    const style = {
      '--modal-video-aspect-w': ratioWidth,
      '--modal-video-aspect-h': ratioHeight
    };

    const modalComponent = (
      <CSSTransition classNames={styles['modal-video-effect']} timeout={animationSpeed}>
        {() => {
          if (!isOpen) {
            return null;
          }

          return (
            <div
              className={styles['modal-video']}
              tabIndex="-1"
              role="dialog"
              data-testid="modal-video-container"
              aria-label={aria.openMessage}
              ref={node => {
                this.modal = node;
              }}
              onKeyDown={this.updateFocus}
              onClick={this.closeModal}>
              <div className={styles['modal-video-body']}>
                <div className={styles['modal-video-inner']}>
                  <div
                    className={styles['modal-video-movie-wrap']}
                    data-testid="modal-video-content"
                    style={style}
                    onClick={e => {
                      // do not close modal if anything inside modal content is clicked
                      e.stopPropagation();
                    }}>
                    <button
                      type="button"
                      className={styles['modal-video-close-btn']}
                      data-testid="modal-video-close"
                      aria-label={aria.dismissBtnMessage}
                      ref={node => {
                        this.modalbtn = node;
                      }}
                      onKeyDown={this.updateFocus}
                      onClick={this.closeModal}
                    />
                    <ReactPlayer
                      data-testid="modal-video-player"
                      url={ModalVideo.getVideoUrl(channel, videoId)}
                      playing
                      width="100%"
                      height="100%"
                      controls
                    />
                    {transcript && (
                      <p className="screen-reader-only" aria-hidden="true" tabIndex="-1">
                        {transcript}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </CSSTransition>
    );

    // Render modal nested in the component tree
    if (!renderNestedModal) return modalComponent;

    // Check for window being available before creating the portal
    if (typeof window === 'object') return createPortal(modalComponent, document.body);

    // fallback in case the conditional renderings above both fail
    return modalComponent;
  }
}

ModalVideo.propTypes = propTypes;

ModalVideo.defaultProps = {
  channel: 'wistia',
  isOpen: false,
  ratio: '16:9',
  animationSpeed: 300,
  aria: {
    openMessage: 'You just opened the modal video',
    dismissBtnMessage: 'Close the modal by clicking here'
  }
};
